import { useEffect, useMemo, useRef } from 'react';
import Text from 'components/text';
import { Box, styled } from '@mui/material';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as EyeOpenIcon } from 'assets/icons/eye-open.svg';
import { ReactComponent as EyeCloseIcon } from 'assets/icons/eye-close.svg';
import { AnalyticsFilters, IStreamsByTrack } from './FugaAnalytics';
import { useTranslation } from 'react-i18next';
import TrackSearchInput from './TrackSearchInput';

const StyledEyeOpenIcon = styled(EyeOpenIcon)(() => ({
  width: '16px',
  opacity: 0.7,
  cursor: 'pointer',
  '& path': {
    fill: '#222',
  },
  ':hover': {
    opacity: 1,
  },
}));

const StyledEyeCloseIcon = styled(EyeCloseIcon)(() => ({
  width: '14px',
  opacity: 0.4,
  cursor: 'pointer',
  '& path': {
    fill: '#222',
  },
  ':hover': {
    opacity: 0.8,
  },
}));

const StyledCloseIcon = styled(CloseIcon)(() => ({
  width: '15px',
  opacity: 0.4,
  cursor: 'pointer',
  '& path': {
    fill: '#222',
  },
  ':hover': {
    opacity: 1,
  },
}));

// Roughly 7 rows tall (each row ≈ 42px + 8px gap) before the list starts scrolling.
const LIST_MAX_HEIGHT = 350;

// Semi-transparent white, button-like wrapper for the per-track hide/remove controls.
const IconButton = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px',
  flexShrink: 0,
  borderRadius: '5px',
  backgroundColor: 'rgba(255, 255, 255, 0.25)',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
}));

export default function ComparableTracksList({
  tracks,
  spProfile,
  addTrack,
  removeTrack,
  toggleTrackVisibility,
  maxTracks,
  canAddTrack = true,
  filters,
}: {
  tracks: IStreamsByTrack[];
  spProfile: string | null;
  addTrack: (trackId: string) => void;
  removeTrack: (trackId: string) => void;
  toggleTrackVisibility: (trackId: string) => void;
  maxTracks: number;
  canAddTrack?: boolean;
  filters?: AnalyticsFilters;
}) {
  const { t } = useTranslation();

  // Scroll the newly added track (appended last) into view. A user-add grows the
  // list by exactly one, which distinguishes it from the initial load / filter refetch.
  const listRef = useRef<HTMLDivElement>(null);
  const prevLenRef = useRef(tracks.length);
  useEffect(() => {
    if (tracks.length === prevLenRef.current + 1 && listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }
    prevLenRef.current = tracks.length;
  }, [tracks.length]);

  // Cache the rendered rows so they only rebuild when the tracks (or handlers) change,
  // not on every unrelated re-render of the parent.
  const trackRows = useMemo(
    () =>
      tracks.map((track, index) => (
        <Box
          key={index}
          onClick={() => toggleTrackVisibility(track.trackId)}
          bgcolor={track.color}
          paddingX={1.5}
          paddingY={0.75}
          borderRadius={1.2}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          sx={{
            cursor: 'pointer',
            userSelect: 'none',
            opacity: track.visible ? 0.85 : 0.4,
            ':hover': {
              opacity: track.visible ? 1 : 0.55,
            },
          }}
        >
          <Box minWidth={0}>
            <Text
              fontSize="15px"
              color="#000"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {track.label}
            </Text>
            <Text fontSize="13px" color="#222" paddingTop={0.3}>
              {t('count_streams', { count: track.streams })}
            </Text>
          </Box>
          <Box display="flex" flexDirection="row" gap={1} alignItems="center" flexShrink={0}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toggleTrackVisibility(track.trackId);
              }}
            >
              {track.visible ? <StyledEyeOpenIcon /> : <StyledEyeCloseIcon />}
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                removeTrack(track.trackId);
              }}
            >
              <StyledCloseIcon />
            </IconButton>
          </Box>
        </Box>
      )),
    [tracks, toggleTrackVisibility, removeTrack, t]
  );

  return (
    <Box>
      <Box paddingBottom={1.2}>
        <Text color="#484848">
          {t('count_of_max_tracks', { current: tracks.length, max: maxTracks })}
        </Text>
      </Box>

      <Box
        ref={listRef}
        display="flex"
        flexDirection="column"
        gap={1}
        sx={{ maxHeight: `${LIST_MAX_HEIGHT}px`, overflowY: 'auto' }}
      >
        {trackRows}
      </Box>

      <Box paddingTop={1.5}>
        {canAddTrack ? (
          <TrackSearchInput
            spProfile={spProfile}
            onToggle={(trackId) =>
              tracks.some((track) => track.trackId === trackId)
                ? removeTrack(trackId)
                : addTrack(trackId)
            }
            selectedTrackIds={tracks.map((track) => track.trackId)}
            filters={filters}
          />
        ) : (
          <Text color="#888" fontSize="14px">
            {t('remove_one_to_add_another')}
          </Text>
        )}
      </Box>
    </Box>
  );
}
